import React, { useState, createRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { Typography, Box, Button, InputField, FormDialog, Grid } from '@kis-technologies/kis-ui';
import NoImagePreview from 'assets/no_image_preview.png';
import { useActions } from 'hooks/useActions';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { validateFileName, isValidImage, isValidResolution } from 'utils/validation';
import ImagePreview from './ImagePreview';
import Requirements from './Requirements';
import { useStyles } from '../styled';
import { ManageImageOverlayProps, MediaTypes } from '../types';
import { getMediaSource } from '../utils';

const ManageImageOverlay = (props: ManageImageOverlayProps) => {
  const { action, configurationId, clientIntegrationId, halfPreview, mediaRecord, mediaType, onClose, resourceName } =
    props;

  const [createMedia, updateMedia, setUploadImageError] = useActions([
    mediaActions.createMedia,
    mediaActions.updateMedia,
    mediaActions.setUploadImageError
  ]);

  const { pending } = useSelector(mediaSelectors.selectAction);

  // replace '/' that could be contained in genre/event/venue names
  const sanitizedResourceName = resourceName ? resourceName.replace(new RegExp('/', 'g'), ' ') : '';
  const [selectedFile, setSelectedFile] = useState<null | File>(null);
  const [fileUrl, setFileUrl] = useState<string>(NoImagePreview);
  const [mediaName, setMediaName] = useState<string>(sanitizedResourceName || '');
  const [filenameError, setFilenameError] = useState<string | null>(null);
  const [invalidFile, setInvalidFile] = useState<boolean>(false);

  const fileUploadRef = createRef<HTMLInputElement>();

  const classes = useStyles({ genreTab: false })();

  const isSaveDisabled =
    action === 'Select' || action === 'Create'
      ? !selectedFile || !mediaName || invalidFile
      : (!selectedFile && mediaName === mediaRecord?.name) || invalidFile;

  useEffect(() => {
    if (action === 'Edit' && mediaRecord) {
      if (!resourceName) setMediaName(mediaRecord.name);
      const mediaSource = getMediaSource(mediaType, mediaRecord);
      if (mediaSource) setFileUrl(mediaSource);
    }
  }, [mediaRecord]);

  const fileNameValidationFn = async (value: string | number | undefined) => {
    if (value && value.toString().trim().length) {
      const schema = validateFileName('Invalid image name format');
      const result = await schema.validate(value.toString().trim()).catch((err) => err);
      setFilenameError((result?.message as string) || null);
      setInvalidFile(result && result.message);
      if ((!result || !result.message) && selectedFile) {
        const err = isValidImage(selectedFile);
        if (err) {
          setUploadImageError(err);
        }
      }
    }
  };

  const onSaveClick = () => {
    if (!selectedFile) {
      // Renaming the media but not modifying the file (Slideshow only)
      updateMedia({
        configurationId,
        oldName: mediaRecord?.name,
        newName: mediaName,
        type: mediaType
      });
    } else {
      createMedia({
        clientIntegrationId,
        configurationId,
        file: selectedFile,
        mediaId: mediaRecord && !resourceName ? mediaRecord?.id : undefined,
        name: mediaName,
        type: mediaType
      });
    }
  };

  const triggerFileSelector = () => {
    if (fileUploadRef.current) fileUploadRef.current.click();
  };

  const getDimensions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = get(e, ['target', 'files', 0]);
    if (file) {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        handleFileSelection(file, height, width);
      };
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileSelection = (file: any, imgHeight: number, imgWidth: number) => {
    if (file) {
      let err = isValidImage(file, mediaType);
      if (!err) {
        err = isValidResolution(mediaType, imgHeight, imgWidth);
      }

      if (err) {
        setUploadImageError(err);
        setSelectedFile(null);
        setFileUrl(NoImagePreview);
      } else {
        setUploadImageError('');
        setSelectedFile(file);
        setFileUrl(URL.createObjectURL(file));
        if (mediaType === 'attract-image' && mediaName) {
          fileNameValidationFn(mediaName);
        } else if (
          ['inactive-image', 'splash-image', 'logo-image', 'logo-image-large', 'background-image'].includes(mediaType)
        ) {
          setMediaName(file.name);
        }
      }
    }
  };

  const handleFilenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFilename = event.target.value.trim();
    fileNameValidationFn(newFilename);
    setMediaName(newFilename);
  };

  return (
    <FormDialog
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
      confirmButtonAction={onSaveClick}
      confirmButtonDisabled={isSaveDisabled}
      confirmButtonIsLoading={pending}
      confirmButtonText="Save"
      open
      title={mediaType === MediaTypes.ATTRACT_IMAGE ? `${action} Slide` : `${action} Image`}
    >
      <Grid width="100%">
        {/* INPUT - NAME (ATTRACT IMAGE ONLY) */}
        {mediaType === MediaTypes.ATTRACT_IMAGE && (
          <Box className={classes.overlayFieldBox}>
            <InputField
              data-testid="name-input-field"
              error={filenameError !== null}
              fullWidth
              helperText={filenameError}
              label="Name"
              onChange={handleFilenameChange}
              size="small"
              sx={{ width: '262px' }}
              value={mediaName || ''}
            />
          </Box>
        )}
        {/* LABEL - IMAGE */}
        <Typography fontSize={16} fontWeight={600} padding="0 0 10px 16px">
          Image
        </Typography>
        <Grid display="flex">
          {/* SELECT IMAGE SECTION */}
          <Box minWidth="262px">
            <input
              type="file"
              id="file"
              ref={fileUploadRef}
              style={{ display: 'none' }}
              onChange={getDimensions}
              accept="image/png, image/jpeg"
              data-testid="file"
            />
            <Box margin="0 0 3">
              <Button customSize="small" width={200} onClick={triggerFileSelector}>
                Select Image
              </Button>
            </Box>
            {/* FILE RECOMMENDATIONS/REQUIREMENTS */}
            <Requirements mediaType={mediaType} />
          </Box>
          {/* IMAGE PREVIEW SECTION */}
          <Box minWidth="216px" padding="0 0 0 16px">
            <Typography fontSize={16}>Image Preview</Typography>
            <Box margin="2 0 0">
              {selectedFile || mediaRecord?.name ? (
                <>
                  <ImagePreview width={200} fileUrl={fileUrl} halfPreview={halfPreview} imageType={mediaType} />
                  <Box width={200} margin="4 0">
                    <Typography fontSize={12}>
                      This preview demonstrates how your image will appear on the screen.
                    </Typography>
                  </Box>
                </>
              ) : (
                <img alt="preview" width={150} height={150} src={fileUrl} data-testid="no-image" />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </FormDialog>
  );
};

export default ManageImageOverlay;
